/**
 * @file Consultation speaker identity — the stable per-room participant id, and the
 *       `participantId → display name` maps the transcript view reads.
 * @module utils/consult/participantIdentity
 *
 * S8 of ClickUp 86d3zmpwv.
 *
 * ═══ WHY THE PATIENT'S ID IS PERSISTED ════════════════════════════════════════════════════
 * Speaker labels come straight from this id (transcribe reports `label_source: "participant_id"`),
 * so a rejoin that mints a new one splits one patient into two speakers in the clinical record.
 * A re-clicked consultation link opens a NEW TAB, so the id lives in `localStorage` keyed by
 * room — timestamped and pruned after 7 days, past any consult and short of accumulating.
 *
 * ═══ NO TRANSPORT ID FORMAT CHANGES HERE ═════════════════════════════════════════════════
 * Only the *lifetime* of the patient id changed. Its shape (`patient_{uuid}`) and every other
 * id on the wire — the in-person practitioner's literal `practitioner`, the paired phone's
 * `mobile-mic`, video's `practitioner_{userId}` — are untouched, so talk and transcribe see
 * exactly what they saw before.
 */

import { ParticipantDisplayNames, VideoParticipant } from "../../types/appointments/ConsultTypes";

// ─── Stable per-room identity ─────────────────────────────────────────────────────────────

/** Key prefix in `localStorage`. One entry per room; nothing else in the app writes them. */
export const CONSULT_PARTICIPANT_KEY_PREFIX = "mg-consult-participant-";

export const consultParticipantKey = (roomId?: string): string => `${CONSULT_PARTICIPANT_KEY_PREFIX}${roomId || "unknown"}`;

const generateUUID = (): string => {
	const webcrypto = typeof globalThis !== "undefined" ? (globalThis.crypto as Crypto | undefined) : undefined;
	if (webcrypto && typeof webcrypto.randomUUID === "function") return webcrypto.randomUUID();
	// `crypto.randomUUID` is undefined outside a secure context and local development is served
	// over plain `http://{MINIKUBE_IP}:3000`, so the fallback is a normal path here, not an edge.
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

// 7 days — comfortably past the longest consult, short of letting entries accumulate on a device.
const PARTICIPANT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Only the generated id is stored — the room id is a bearer credential and stays in the key. */
type StoredParticipant = { id: string; at: number };

const readStoredId = (key: string): string | null => {
	try {
		const raw = window.localStorage.getItem(key);
		if (!raw) return null;

		const stored = JSON.parse(raw) as Partial<StoredParticipant>;
		const fresh = typeof stored?.at === "number" && Date.now() - stored.at < PARTICIPANT_TTL_MS;
		if (fresh && typeof stored?.id === "string" && stored.id) return stored.id;
	} catch {
		/* unreadable, or a plain string from the pre-localStorage implementation — treat as absent */
	}

	try {
		window.localStorage.removeItem(key);
	} catch {
		/* storage unavailable — nothing to prune, and the caller still gets a usable id */
	}

	return null;
};

/**
 * The participant id this browser uses for the given room — the same one it used before a
 * refresh, and before a re-clicked consultation link reopened the room in a fresh tab.
 *
 * Every access is wrapped: `localStorage` throws in Safari private mode and when storage is
 * disabled by policy, and a throw here would stop the consultation page loading at all. A
 * patient whose browser refuses to store it simply gets a fresh id per load.
 */
export const getStableParticipantId = (roomId?: string): string => {
	const key = consultParticipantKey(roomId);

	const existing = readStoredId(key);
	if (existing) return existing;

	const participantId = `patient_${generateUUID()}`;
	const entry: StoredParticipant = { id: participantId, at: Date.now() };

	try {
		window.localStorage.setItem(key, JSON.stringify(entry));
	} catch {
		/* see above: an unstorable id is still a usable id for this page load */
	}

	return participantId;
};

// ─── Display-name maps ────────────────────────────────────────────────────────────────────

/** The in-person practitioner's own microphone — a literal, not `practitioner_{id}`. */
export const IN_PERSON_PRACTITIONER_ID = "practitioner";
/** A phone paired to an in-person room by QR code (`components/mobile/MobileMic.tsx`). */
export const MOBILE_MIC_PARTICIPANT_ID = "mobile-mic";
export const MOBILE_MIC_DISPLAY_NAME = "Room microphone";

/**
 * Video roster → display names, keyed by the id the roster's audio is tagged with.
 *
 * `self` is seeded first and the roster overwrites it, because the server is not guaranteed
 * to include us in every roster payload — but when it does, its copy is the authoritative one.
 * A participant carrying no `audioParticipantId` (an older client) is skipped rather than
 * guessed at: the transcript falls back to the raw id, which is honest.
 */
export const buildVideoDisplayNames = (
	participants: VideoParticipant[],
	self?: Pick<VideoParticipant, "audioParticipantId" | "displayName">,
): ParticipantDisplayNames => {
	const displayNames: ParticipantDisplayNames = {};

	if (self?.audioParticipantId && self.displayName) displayNames[self.audioParticipantId] = self.displayName;

	(participants || []).forEach((p) => {
		if (p?.audioParticipantId && p.displayName) displayNames[p.audioParticipantId] = p.displayName;
	});

	return displayNames;
};

/**
 * In-person rooms have no roster to read — there are exactly two possible microphones, both
 * in the same room, so the map is static.
 */
export const buildInPersonDisplayNames = (practitionerName?: string): ParticipantDisplayNames => ({
	[IN_PERSON_PRACTITIONER_ID]: practitionerName?.trim() || "Practitioner",
	[MOBILE_MIC_PARTICIPANT_ID]: MOBILE_MIC_DISPLAY_NAME,
});
